import { nudgeSession } from '../utils/nudgeSession';
import { patchNudgeState } from '../services/optionalNudges';
import { openStoreReviewPage } from '../utils/storeReview';
import { Linking, Platform } from 'react-native';
import { createNavigationContainerRef } from '@react-navigation/native';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { auth, firestore, FIRESTORE_COLLECTIONS, PUBLIC_ACTIVITY_COLLECTIONS } from '../firebase';
import { getChatCollectionName } from '../hooks/useProfile';
import { normalizeActivity } from '../hooks/useFeed';
import { resolveOrganizerSummaryById, resolveVenueSummaryById } from '../hooks/useVenueVibes';
import type { ChatItem, RootStackParamList, TonyteActivity } from '../types';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { getUserDisplayName, getUserProfile } from '../utils/userProfiles';
import { toLocalDateKey } from '../utils/activityDates';

export const navigationRef = createNavigationContainerRef<RootStackParamList>();

// Emulator web builds only (see firebase.ts) — lets local tooling open a screen.
declare const __TONYTE_USE_EMULATORS__: boolean | undefined;
if (typeof __TONYTE_USE_EMULATORS__ !== 'undefined' && __TONYTE_USE_EMULATORS__) {
  (globalThis as any).__tonyteNavigate = (name: string, params?: object) => (navigationRef.navigate as any)(name, params);
}

// Two independent readiness gates — a notification tap can arrive before
// EITHER the navigation container has mounted (cold start) OR auth has
// finished restoring the persisted session (both race the same cold start,
// on different timelines). Navigating before both are true either no-ops
// silently against a stack that doesn't contain the target screen yet
// (React Navigation swallows navigate-to-unknown-route rather than
// crashing) or targets a not-yet-mounted ref — queue until both are ready
// instead of guessing.
let navReady = false;
let authReady = false;
let pendingRun: (() => void) | null = null;
// How long a queued push-tap navigation waits for navReady/authReady before
// running anyway (see navigateFromNotification's queue branch) — bounds the
// "user permanently stuck pending email verification taps a chat
// notification and nothing ever happens" case to a fixed, bearable wait
// instead of forever.
const PENDING_NAV_TIMEOUT_MS = 15_000;

function flushIfReady() {
  if (!navReady || !authReady || !pendingRun) return;
  if (__DEV__) console.log('[PushTapDebug] flushIfReady — both gates ready, running queued navigation');
  const run = pendingRun;
  pendingRun = null;
  run();
}

export function markNavReady(): void {
  if (__DEV__) console.log('[PushTapDebug] markNavReady()');
  navReady = true;
  flushIfReady();
  flushPendingDeepLink();
}

export function markAuthReady(): void {
  if (__DEV__) console.log('[PushTapDebug] markAuthReady()');
  authReady = true;
  flushIfReady();
}

// Separate, stricter gates for share/QR deep links (profile/event/venue/
// organizer/student-verification — not push notifications). `authReady`
// above fires as soon as auth state is merely *known* — including
// "definitely signed out", where the Login screen is the only registered
// route. A link tapped while logged out must survive until the destination
// screen is actually mounted.
//
// Two distinct readiness conditions exist because the destination screens
// live in different branches of AppNavigator:
//  - `mainReady`: PublicProfile/EventDetail/VenueProfile/OrganizerProfile are
//    only registered in the final branch — fully authenticated, no pending
//    profile/partner-password setup.
//  - `verificationReady`: StudentVerificationComplete is registered both in
//    that final branch AND in the requiresProfileSetup branch (a user who
//    hasn't finished onboarding can still legitimately tap a verification
//    email) — so it must flush on the broader condition, not wait for
//    profile setup to be done too.
let mainReady = false;
let verificationReady = false;

export type DeepLinkTarget =
  | { type: 'profile'; userId: string }
  | { type: 'event'; eventId: string }
  | { type: 'venue'; venueId: string }
  | { type: 'organizer'; organizerId: string }
  | { type: 'studentVerification'; token: string }
  | { type: 'chat'; chatId: string }
  | { type: 'promotion'; promotionId: string };

function isTargetReady(target: DeepLinkTarget): boolean {
  return target.type === 'studentVerification' ? verificationReady : mainReady;
}

let pendingDeepLink: DeepLinkTarget | null = null;

// Mirrors ProfileScreen.tsx's inline `fetchEventById` — same collections,
// same doc-id-first / eventId-or-canonicalEventId-field fallback (some
// curated events are shared under a canonical id distinct from their own
// doc id, see getResolvedEventId) — plus the `plans` collection, since a
// canonical event link can also point at a Community Plan (EventDetail
// itself makes no id-space distinction between the two, see
// normalizeActivity's entryType). EventDetailScreen only resolves activities
// out of the currently-loaded useFeed() list or an explicitly passed
// `event` — an eventId with neither would leave it on a permanent loading
// spinner (see its own deep-link comment), so a deep link must fetch and
// normalize the real doc itself rather than navigate with a bare eventId.
async function resolveEventActivityById(eventId: string): Promise<TonyteActivity | null> {
  for (const collectionName of PUBLIC_ACTIVITY_COLLECTIONS) {
    try {
      const snap = await getDoc(doc(firestore, collectionName, eventId));
      if (snap.exists()) return normalizeActivity(snap.id, snap.data() as any, collectionName);
    } catch {
      // skip permission errors for individual collections
    }
  }
  try {
    const planSnap = await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, eventId));
    if (planSnap.exists()) return normalizeActivity(planSnap.id, planSnap.data() as any, FIRESTORE_COLLECTIONS.plans);
  } catch {
    // skip
  }
  for (const collectionName of PUBLIC_ACTIVITY_COLLECTIONS) {
    try {
      const [byEventId, byCanonicalId] = await Promise.all([
        getDocs(query(collection(firestore, collectionName), where('eventId', '==', eventId), limit(1))),
        getDocs(query(collection(firestore, collectionName), where('canonicalEventId', '==', eventId), limit(1))),
      ]);
      const matchedDoc = [...byEventId.docs, ...byCanonicalId.docs][0];
      if (matchedDoc) return normalizeActivity(matchedDoc.id, matchedDoc.data() as any, collectionName);
    } catch {
      // skip
    }
  }
  return null;
}

// Which collection each chat "kind" lives in, and how its membership array
// is named — matches getChatCollectionName's own mapping (useProfile.ts) and
// the eventChats/planChats (`memberIds`) vs matches/privateChats (`users`)
// split documented in the chat system audit.
const CHAT_KIND_COLLECTIONS: Array<{ kind: ChatItem['kind']; collectionName: string; memberField: 'memberIds' | 'users' }> = [
  { kind: 'event', collectionName: FIRESTORE_COLLECTIONS.eventChats, memberField: 'memberIds' },
  { kind: 'plan', collectionName: FIRESTORE_COLLECTIONS.planChats, memberField: 'memberIds' },
  { kind: 'match', collectionName: FIRESTORE_COLLECTIONS.matches, memberField: 'users' },
  { kind: 'vibe', collectionName: FIRESTORE_COLLECTIONS.privateChats, memberField: 'users' },
];

// A /chat/:chatId deep link must never hand a viewer content from a chat
// they are not actually part of — unlike venue/organizer/event, which are
// public by nature, chats are private. This checks membership directly
// against each of the four chat collections (the doc ID alone doesn't say
// which "kind" it is) and only returns a target once membership is
// confirmed; any other outcome (not found, exists but not a member,
// permission-denied) falls through to the same "land on Main" fallback the
// other deep-link types already use for an unresolvable id.
async function resolveChatAccessById(
  chatId: string,
  userId: string,
): Promise<{ kind: ChatItem['kind']; name: string; photo?: string } | null> {
  for (const { kind, collectionName, memberField } of CHAT_KIND_COLLECTIONS) {
    try {
      const snap = await getDoc(doc(firestore, collectionName, chatId));
      if (!snap.exists()) continue;
      const data = snap.data() as Record<string, unknown>;
      const members = data[memberField];
      if (!Array.isArray(members) || !members.includes(userId)) continue;
      const name = (kind === 'event' ? data.eventTitle : kind === 'plan' ? data.planTitle : data.name);
      const photo = (data.photo ?? data.eventImage) as string | undefined;
      return { kind, name: typeof name === 'string' ? name : '', photo };
    } catch {
      // Permission-denied (not a member) or a transient read error on this
      // collection — try the remaining collections before giving up.
    }
  }
  return null;
}

async function runDeepLink(target: DeepLinkTarget) {
  if (!navigationRef.isReady()) return;
  switch (target.type) {
    case 'profile':
      navigationRef.navigate('PublicProfile', { userId: target.userId });
      return;
    case 'studentVerification':
      navigationRef.navigate('StudentVerificationComplete', { token: target.token });
      return;
    case 'promotion':
      // PromotionScreen resolves the id itself and shows a graceful
      // "no longer available" state for ended/removed collections.
      navigationRef.navigate('Promotion', { promotionId: target.promotionId });
      return;
    case 'venue': {
      const venue = await resolveVenueSummaryById(target.venueId);
      if (!venue) {
        // VenueProfileScreen has no "not found" branch of its own — it spins
        // forever once its own list never resolves the id (same class of gap
        // EventDetailScreen has) — so an unresolvable id must not navigate
        // there at all.
        if (__DEV__) console.log('[Tonyte][DeepLink] venue not found, falling back to Main', { venueId: target.venueId });
        navigationRef.navigate('Main', undefined);
        return;
      }
      navigationRef.navigate('VenueProfile', { venueId: target.venueId, venue });
      return;
    }
    case 'organizer': {
      const organizer = await resolveOrganizerSummaryById(target.organizerId);
      if (!organizer) {
        // OrganizerProfileScreen *does* already alert+goBack on its own for a
        // missing id, but resolving up front avoids a flash of that screen
        // for something we already know doesn't exist, and keeps this
        // branch symmetric with the venue/event cases.
        if (__DEV__) console.log('[Tonyte][DeepLink] organizer not found, falling back to Main', { organizerId: target.organizerId });
        navigationRef.navigate('Main', undefined);
        return;
      }
      navigationRef.navigate('OrganizerProfile', { organizerId: target.organizerId, organizer });
      return;
    }
    case 'event': {
      const activity = await resolveEventActivityById(target.eventId);
      if (!activity) {
        // Genuinely gone (deleted/never existed) — land on Main rather than a
        // screen that can only show a permanent spinner for an unresolvable
        // id, same fallback already used above for a missing notification
        // target.
        if (__DEV__) console.log('[Tonyte][DeepLink] event not found, falling back to Main', { eventId: target.eventId });
        navigationRef.navigate('Main', undefined);
        return;
      }
      trackEventDetailOpen({ eventId: target.eventId, organizerId: activity.hostId, source: ANALYTICS_SOURCES.SHARED_LINK });
      navigationRef.navigate('EventDetail', { eventId: target.eventId, event: activity });
      return;
    }
    case 'chat': {
      // Unlike every other case above (public content, no auth dependency),
      // this needs the signed-in uid for the membership check below — and
      // on a hard refresh, `mainReady` can flip slightly before Firebase
      // Auth's own async session restoration finishes, leaving
      // `auth.currentUser` still null for one tick even though the user IS
      // authenticated (reproduced live: refreshing on a valid /chat/:id
      // destination bounced to Main because of exactly this race).
      // onAuthStateChanged always fires once immediately with whatever the
      // current state already is, so this resolves on the next microtask
      // when currentUser is already set, and waits only when it's genuinely
      // still mid-restore.
      const currentUserId = auth.currentUser?.uid
        ?? await new Promise<string | undefined>(resolve => {
          const unsubscribe = auth.onAuthStateChanged(user => {
            unsubscribe();
            resolve(user?.uid);
          });
        });
      const access = currentUserId ? await resolveChatAccessById(target.chatId, currentUserId) : null;
      if (!access) {
        // Not found, or the signed-in viewer isn't a member — never land on
        // a chat thread they don't belong to.
        if (__DEV__) console.log('[Tonyte][DeepLink] chat not found or not a member, falling back to Main', { chatId: target.chatId });
        navigationRef.navigate('Main', undefined);
        return;
      }
      navigationRef.navigate('ChatDetail', { chatId: target.chatId, name: access.name, kind: access.kind, photo: access.photo });
      return;
    }
    default:
      return;
  }
}

function flushPendingDeepLink() {
  if (!navReady || !pendingDeepLink || !isTargetReady(pendingDeepLink)) return;
  const target = pendingDeepLink;
  pendingDeepLink = null;
  void runDeepLink(target);
}

export function markMainReady(): void {
  mainReady = true;
  flushPendingDeepLink();
}

export function markVerificationReady(): void {
  verificationReady = true;
  flushPendingDeepLink();
}

/** Queues a share-link deep link (profile/event/venue/organizer/student-verification) to open once nav + its destination's readiness gate are both ready; runs immediately if they already are. */
export function queueDeepLink(target: DeepLinkTarget): void {
  pendingDeepLink = target;
  flushPendingDeepLink();
}

// Defensive dedup: getInitialNotification() (cold start) and
// onNotificationOpenedApp() (background tap) are documented as mutually
// exclusive for a single physical tap, but a redelivered/duplicate remote
// message (or, in principle, both listeners observing the same message)
// would otherwise navigate twice for one tap. A short identity+time window
// is enough — a real second tap on the same notification is not a
// meaningful use case to support distinctly.
//
// Re-enabled — the earlier [PushTapDebug] investigation that disabled this
// to test a theory confirmed it was NOT the cause (the real causes were the
// isMainStackMounted/mainNavigatorReady readiness gate missing the
// email-verification state, MainTabs' hook-placement mount/unmount race,
// and an unmatched Android clickAction — all fixed separately). Safe to
// restore.
const DEDUPE_ENABLED = true;
let lastProcessedKey: string | null = null;
let lastProcessedAt = 0;
const DEDUPE_WINDOW_MS = 8000;

function notificationDedupeKey(data: NotificationTapData): string {
  return [data.type, data.notificationId, data.planId, data.requestId, data.chatId, data.messageId, data.eventId, data.venueId]
    .filter(Boolean).join('|');
}

export type NotificationTapData = {
  type?: string;
  chatId?: string;
  chatKind?: string;
  messageId?: string;
  senderId?: string;
  senderName?: string;
  planId?: string;
  requestId?: string;
  requesterId?: string;
  requesterName?: string;
  otherUserId?: string;
  isStudiZone?: string;
  // Event & partner notifications (functions/notifications.js). Navigation
  // keys off `type` + these ids, never the title/body text.
  notificationId?: string;
  route?: string;
  eventId?: string;
  venueId?: string;
  partnerId?: string;
  // vibe_plan_created: the plan to open (not `planId`, which published
  // builds would act on — functions/planNotifications.js).
  targetPlanId?: string;
  planLayer?: string;
  feedDate?: string;
  feedFilter?: string;
  updateUrl?: string;
  iosStoreUrl?: string;
  androidStoreUrl?: string;
};

// Only these hosts may be opened from an app_update push.
const STORE_URL_PATTERN = Platform.OS === 'ios'
  ? /^https:\/\/apps\.apple\.com\//
  : /^https:\/\/play\.google\.com\/store\/apps\/details\?id=com\.triptribe\.mobile/;

// Feed-opening event notifications → Feed tab on `feedDate`.
const FEED_NOTIFICATION_TYPES = ['weekly_events', 'daily_events', 'weekend_events'];
// Event-detail notifications from partners the user follows.
const EVENT_NOTIFICATION_TYPES = ['partner_new_event', 'partner_event_day'];
// Partner-profile notifications: followers see the partner's calendar; the
// partner (new follower) lands on their own profile, where the owner-only
// followers list lives.
const VENUE_NOTIFICATION_TYPES = ['partner_calendar_update', 'partner_new_follower'];

const VALID_CHAT_KINDS: ChatItem['kind'][] = ['match', 'event', 'plan', 'vibe'];

function isValidChatKind(value: unknown): value is ChatItem['kind'] {
  return typeof value === 'string' && (VALID_CHAT_KINDS as string[]).includes(value);
}

// [PushTapDebug] step 9 — logs isReady()/current route/target route+params
// immediately before every navigate() call in this module, and confirms the
// call actually returned. Every navigate() call site in resolveAndNavigate
// goes through this so there's one place proving the call was even made.
function debugNavigate(routeName: string, params: unknown): void {
  const ready = navigationRef.isReady();
  const currentRoute = ready ? navigationRef.getCurrentRoute()?.name ?? null : null;
  if (__DEV__) {
    console.log('[PushTapDebug] about to navigate', {
      isReady: ready,
      currentRoute,
      targetRoute: routeName,
      params,
    });
  }
  if (!ready) {
    if (__DEV__) console.log('[PushTapDebug] navigate SKIPPED — navigationRef.isReady() is false');
    return;
  }
  // @ts-expect-error — routeName/params are dynamic across the several call sites below; each call site's literal is already type-checked at its own call.
  navigationRef.navigate(routeName, params);
  if (__DEV__) console.log('[PushTapDebug] navigate() invoked and returned', { targetRoute: routeName });
}

// A vibe (1:1 private) chat doc never stores its own `name`/`photo` — the
// chat list resolves those client-side from the OTHER participant's public
// profile (see useProfile.ts's useChats vibeDocs mapping). event/plan chats
// do store title/planTitle/photo directly on their own doc. Shared here so
// both the chat_message tap (below) and the vibe_request_accepted tap use
// the exact same resolution instead of a notification-only "Chat" fallback
// title for every vibe chat.
async function resolveChatDetailHeader(
  kind: ChatItem['kind'],
  chatData: Record<string, unknown>,
): Promise<{ name: string; photo: string | undefined; participantIds: string[] | undefined }> {
  const participantIds = Array.isArray(chatData.memberIds)
    ? (chatData.memberIds as string[])
    : Array.isArray(chatData.users)
      ? (chatData.users as string[])
      : undefined;

  if (kind === 'vibe') {
    const currentUid = auth.currentUser?.uid;
    const otherUserId = (participantIds || []).find(id => id !== currentUid);
    if (otherUserId) {
      const profile = await getUserProfile(otherUserId).catch(() => null);
      if (profile) return { name: getUserDisplayName(profile), photo: profile.profileImage, participantIds };
    }
  }

  return {
    name: String(chatData.planTitle || chatData.title || chatData.name || 'Chat'),
    photo: typeof chatData.photo === 'string' ? chatData.photo : undefined,
    participantIds,
  };
}

// Reminder/promotional pushes: the session's optional popup slot is used up
// (no in-app nudge right after tapping one) and the tap is recorded for the
// shared cooldown (functions/optionalReminders.js reads lastPushOpenedAt).
function noteReminderPushOpened(type: string) {
  nudgeSession.markOpenedFromReminderPush();
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  const now = Date.now();
  void patchNudgeState(uid, {
    lastPushOpenedAt: now,
    lastPushOpenedKind: type,
    ...(type === 'review_reminder' ? { review: { lastPushOpenedAt: now } } : {}),
  }).catch(() => undefined);
}

async function resolveAndNavigate(data: NotificationTapData) {
  try {
    if (data.type === 'chat_message' && data.chatId && isValidChatKind(data.chatKind)) {
      if (__DEV__) console.log('[PushTapDebug] branch: chat_message');
      const kind = data.chatKind;
      const collectionName = getChatCollectionName(kind);
      const chatSnap = await getDoc(doc(firestore, collectionName, data.chatId));
      if (!chatSnap.exists()) {
        // Covers both "never existed" and — for a vibe chat specifically —
        // "since Unvibed": unvibeUserHandler (functions/index.js) fully
        // deletes the privateChats doc, so a stale notification tapped
        // after that must never recreate or reopen it.
        if (__DEV__) console.log('[PushTapDebug] chat doc missing, falling back to Main');
        debugNavigate('Main', undefined);
        return;
      }
      const chatData = chatSnap.data() as Record<string, unknown>;
      const header = await resolveChatDetailHeader(kind, chatData);
      debugNavigate('ChatDetail', { chatId: data.chatId, kind, ...header });
      return;
    }

    if (data.type === 'vibe_request') {
      if (__DEV__) console.log('[PushTapDebug] branch: vibe_request');
      // Vibe Requests lives as a sub-tab of the Chat bottom-tab (ChatScreen.tsx's
      // ChatListScreen), not a standalone stack route — MainTabs threads this
      // through as a prop (see types.ts's MessagesTabFocus).
      debugNavigate('Main', { initialTab: 'Chat', focusMessagesTab: { tab: 'requests', nonce: Date.now() } });
      return;
    }

    if (data.type === 'vibe_request_accepted') {
      if (__DEV__) console.log('[PushTapDebug] branch: vibe_request_accepted');
      const chatSnap = data.chatId ? await getDoc(doc(firestore, FIRESTORE_COLLECTIONS.privateChats, data.chatId)) : null;
      if (chatSnap?.exists()) {
        const chatData = chatSnap.data() as Record<string, unknown>;
        const header = await resolveChatDetailHeader('vibe', chatData);
        debugNavigate('ChatDetail', { chatId: data.chatId, kind: 'vibe', ...header });
        return;
      }
      // Chat unresolvable (never existed, or Unvibed since the push was
      // sent — never recreate/reopen a deleted chat from a stale
      // notification) — fall back to the other user's profile, still a
      // useful destination for "it's a Vibe" without a chat to show yet.
      if (__DEV__) console.log('[PushTapDebug] accepted-chat missing, falling back to PublicProfile', { chatId: data.chatId });
      if (data.otherUserId) {
        debugNavigate('PublicProfile', { userId: data.otherUserId });
      } else {
        debugNavigate('Main', undefined);
      }
      return;
    }

    if (data.type === 'plan_join_request') {
      if (__DEV__) console.log('[PushTapDebug] branch: plan_join_request');
      const userId = auth.currentUser?.uid || '';
      if (!userId || !data.planId) {
        if (__DEV__) console.log('[PushTapDebug] missing userId or planId, falling back to Main', { userId, planId: data.planId });
        debugNavigate('Main', undefined);
        return;
      }

      const [planSnap, requestSnap] = await Promise.all([
        getDoc(doc(firestore, 'plans', data.planId)),
        data.requestId ? getDoc(doc(firestore, 'planJoinRequests', data.requestId)) : Promise.resolve(null),
      ]);
      if (!planSnap.exists()) {
        // Plan itself is gone — Plan Details has nothing to render for a
        // deleted plan (EventDetailScreen shows a permanent loading spinner
        // for an unresolvable id), so land somewhere actually usable.
        if (__DEV__) console.log('[PushTapDebug] plan doc missing, falling back to Main', { planId: data.planId });
        debugNavigate('Main', undefined);
        return;
      }
      const requestGone = Boolean(data.requestId) && (!requestSnap || !requestSnap.exists() || requestSnap.data()?.status !== 'pending');
      if (requestGone) {
        // The request was accepted/declined/cancelled (e.g. from another
        // admin device) before this tap was handled — fall back to Plan
        // Details instead of opening a requests screen for a request that's
        // no longer pending. EventDetailScreen resolves its activity only
        // from useFeed() (which never queries the `plans` collection) or
        // this navigation's own `event` param — without passing the
        // already-fetched plan data here, it would sit on a permanent
        // loading spinner instead of showing Plan Details.
        if (__DEV__) console.log('[PushTapDebug] request gone/resolved, routing to EventDetail instead', { requestId: data.requestId });
        trackEventDetailOpen({ eventId: data.planId, organizerId: planSnap.data()?.hostId, source: ANALYTICS_SOURCES.NOTIFICATION });
        debugNavigate('EventDetail', {
          eventId: data.planId,
          event: normalizeActivity(planSnap.id, planSnap.data(), 'plans'),
        });
        return;
      }

      const params = {
        userId,
        planId: data.planId,
        ...(data.requestId ? { highlightRequestId: data.requestId } : {}),
      };
      // StudiZone plan requests are excluded from the general admin view
      // (useAdminAllPendingRequests filters them out — PlanRequestsScreen.tsx),
      // so route to the StudiZone-specific screen for those, matching the
      // route the request document's own `layer`/`isStudentOnly` implies.
      if (data.isStudiZone === 'true') {
        if (__DEV__) console.log('[PushTapDebug] routing to StudiZoneProfileRequests', params);
        debugNavigate('StudiZoneProfileRequests', params);
      } else {
        if (__DEV__) console.log('[PushTapDebug] routing to PlanRequests', params);
        debugNavigate('PlanRequests', params);
      }
      return;
    }

    if (data.type === 'vibe_plan_created') {
      if (__DEV__) console.log('[PushTapDebug] branch: vibe_plan_created');
      if (!data.targetPlanId) {
        debugNavigate('Main', undefined);
        return;
      }
      // A plan that's gone, ended-and-deleted, or no longer readable (e.g.
      // the vibe was removed) lands on Main instead of a spinner.
      const planSnap = await getDoc(doc(firestore, 'plans', data.targetPlanId)).catch(() => null);
      if (!planSnap || !planSnap.exists()) {
        if (__DEV__) console.log('[PushTapDebug] plan unavailable, falling back to Main', { planId: data.targetPlanId });
        debugNavigate('Main', undefined);
        return;
      }
      const planData = planSnap.data();
      trackEventDetailOpen({ eventId: data.targetPlanId, organizerId: planData?.hostId, source: ANALYTICS_SOURCES.NOTIFICATION });
      const event = normalizeActivity(planSnap.id, planData, 'plans');
      const studiZone = planData?.layer === 'studizone' || planData?.isStudentOnly === true || data.planLayer === 'studizone';
      debugNavigate(studiZone ? 'StudiZonePlanDetailScreen' : 'EventDetail', { eventId: data.targetPlanId, event });
      return;
    }

    if (data.type === 'plan_join_accepted') {
      if (__DEV__) console.log('[PushTapDebug] branch: plan_join_accepted');
      if (!data.planId) {
        if (__DEV__) console.log('[PushTapDebug] missing planId, falling back to Main');
        debugNavigate('Main', undefined);
        return;
      }
      const planSnap = await getDoc(doc(firestore, 'plans', data.planId));
      if (!planSnap.exists()) {
        if (__DEV__) console.log('[PushTapDebug] plan doc missing, falling back to Main', { planId: data.planId });
        debugNavigate('Main', undefined);
        return;
      }
      trackEventDetailOpen({ eventId: data.planId, organizerId: planSnap.data()?.hostId, source: ANALYTICS_SOURCES.NOTIFICATION });
      // See the plan_join_request branch above for why `event` must be
      // passed explicitly — EventDetailScreen can't resolve a bare Plan id
      // via useFeed() (it never queries the `plans` collection).
      if (__DEV__) console.log('[PushTapDebug] routing to EventDetail', { eventId: data.planId });
      debugNavigate('EventDetail', {
        eventId: data.planId,
        event: normalizeActivity(planSnap.id, planSnap.data(), 'plans'),
      });
      return;
    }

    if (data.type && FEED_NOTIFICATION_TYPES.includes(data.type)) {
      noteReminderPushOpened(data.type);
      const feedDate = /^\d{4}-\d{2}-\d{2}$/.test(data.feedDate || '') ? data.feedDate! : toLocalDateKey(new Date());
      // The evening push asks for the Tonight chip (feedFilter: 'tonight').
      const chips = data.feedFilter === 'tonight' ? ['tonight' as const] : undefined;
      debugNavigate('Main', { initialTab: 'Feed', focusFeed: { date: feedDate, nonce: Date.now(), ...(chips ? { chips } : {}) } });
      return;
    }

    if (data.type && EVENT_NOTIFICATION_TYPES.includes(data.type) && data.eventId) {
      const activity = await resolveEventActivityById(data.eventId);
      if (!activity) {
        // Deleted since the push was sent — the partner's profile is still a
        // useful destination; otherwise land on Main.
        const venue = data.venueId ? await resolveVenueSummaryById(data.venueId) : null;
        if (venue) debugNavigate('VenueProfile', { venueId: data.venueId, venue });
        else debugNavigate('Main', undefined);
        return;
      }
      trackEventDetailOpen({ eventId: data.eventId, organizerId: activity.hostId, source: ANALYTICS_SOURCES.NOTIFICATION });
      debugNavigate('EventDetail', { eventId: data.eventId, event: activity });
      return;
    }

    if (data.type && VENUE_NOTIFICATION_TYPES.includes(data.type) && data.venueId) {
      const venue = await resolveVenueSummaryById(data.venueId);
      // VenueProfileScreen can't render an unresolvable id (see runDeepLink's venue case).
      if (venue) debugNavigate('VenueProfile', { venueId: data.venueId, venue });
      else debugNavigate('Main', undefined);
      return;
    }

    if (data.type === 'profile_photo_reminder') {
      noteReminderPushOpened(data.type);
      debugNavigate('CompleteProfile', { initialStep: 'profile_photo' });
      return;
    }

    if (data.type === 'vibe_verification_reminder') {
      noteReminderPushOpened(data.type);
      // VerificationCenter shows the real current status (pending/rejected/…)
      // and the matching next action.
      debugNavigate('VerificationCenter', undefined);
      return;
    }

    if (data.type === 'feedback_survey_reminder') {
      noteReminderPushOpened(data.type);
      // Land on the home screen and open the survey straight away (no invite popup).
      debugNavigate('Main', undefined);
      nudgeSession.requestOpenSurvey();
      return;
    }

    if (data.type === 'review_reminder') {
      noteReminderPushOpened(data.type);
      // A tap needs a guaranteed destination; the in-app review API may show
      // nothing, so open the store listing (iOS write-review page).
      if (!(await openStoreReviewPage())) debugNavigate('Main', undefined);
      return;
    }

    if (data.type === 'app_update') {
      // Targeted pushes carry updateUrl; the legacy (platform-neutral)
      // announcement carries both store links.
      const storeUrl = data.updateUrl || (Platform.OS === 'ios' ? data.iosStoreUrl : data.androidStoreUrl);
      if (storeUrl && STORE_URL_PATTERN.test(storeUrl)) {
        await Linking.openURL(storeUrl);
        return;
      }
      debugNavigate('Main', undefined);
      return;
    }

    if (data.type === 'plan_member_removed') {
      // Deliberately opens Main, never ChatDetail/EventDetail for this plan —
      // the whole point of removal is that this user no longer has access,
      // and both the plan chat's messages rule (bannedUserIds) and its
      // membership listener already refuse/exit them if they somehow did
      // reach it, but there's no reason to route them toward it at all.
      if (__DEV__) console.log('[PushTapDebug] branch: plan_member_removed');
      debugNavigate('Main', undefined);
      return;
    }

    // Unknown/malformed payload — land somewhere safe rather than doing nothing.
    if (__DEV__) console.log('[PushTapDebug] unknown/malformed payload type, falling back to Main', { type: data.type });
    debugNavigate('Main', undefined);
  } catch (error) {
    console.warn('[Tonyte][RN][Push] failed to navigate from notification tap', error);
    if (__DEV__) console.log('[PushTapDebug] resolveAndNavigate threw', error);
    debugNavigate('Main', undefined);
  }
}

/** Entry point for both cold-start (getInitialNotification) and background-tap (onNotificationOpenedApp) handling. */
export function navigateFromNotification(data: NotificationTapData | undefined): void {
  // [PushTapDebug] step 2 — first line, every field the spec asked for, plus
  // whether a pending navigation is already queued (would mean a previous
  // tap never flushed).
  if (__DEV__) {
    console.log('[PushTapDebug] navigateFromNotification called', {
      timestamp: new Date().toISOString(),
      rawData: data,
      type: data?.type,
      planId: data?.planId,
      requestId: data?.requestId,
      messageId: data?.messageId,
      navReady,
      authReady,
      pendingRunAlreadyQueued: pendingRun !== null,
    });
  }

  if (!data?.type) {
    if (__DEV__) console.log('[PushTapDebug] no data.type — returning without navigating');
    return;
  }

  const key = notificationDedupeKey(data);
  const now = Date.now();
  const wouldBeDuplicate = Boolean(key) && key === lastProcessedKey && now - lastProcessedAt < DEDUPE_WINDOW_MS;
  if (wouldBeDuplicate) {
    if (DEDUPE_ENABLED) {
      if (__DEV__) console.log('[PushTapDebug] blocked by dedup', { key, msSinceLastProcessed: now - lastProcessedAt });
      return;
    }
    // Dedup temporarily disabled — see DEDUPE_ENABLED above. Log both events
    // instead of suppressing so the debug pass can see whether this window
    // would otherwise have swallowed a real, distinct tap.
    if (__DEV__) console.log('[PushTapDebug] WOULD have been blocked by dedup (dedup disabled for debug) — proceeding anyway', { key, msSinceLastProcessed: now - lastProcessedAt });
  }
  lastProcessedKey = key;
  lastProcessedAt = now;

  if (__DEV__) {
    console.log('[PushTapDebug] type=' + data.type, { planId: data.planId, requestId: data.requestId });
    console.log('[PushTapDebug] navReady=' + navReady + ' authReady=' + authReady);
  }

  const run = () => { void resolveAndNavigate(data); };
  if (navReady && authReady) {
    if (__DEV__) console.log('[PushTapDebug] both gates ready — running immediately');
    run();
  } else {
    if (__DEV__) console.log('[PushTapDebug] queued because nav/auth not ready', { navReady, authReady });
    pendingRun = run;
    // Safety net: a user who never reaches isMainStackMounted (e.g. stuck
    // pending email verification) would otherwise leave this queued
    // forever with the tap visibly doing nothing. After a bounded wait,
    // run it anyway — resolveAndNavigate()'s own per-type resolution
    // and debugNavigate()'s navigationRef.isReady() check already handle
    // an unregistered/not-yet-mounted destination safely (React Navigation
    // no-ops on an unknown route rather than crashing), so forcing this
    // after the timeout can't do worse than the silent-forever status quo.
    setTimeout(() => {
      if (pendingRun !== run) return; // already flushed normally, or superseded
      if (__DEV__) console.log('[PushTapDebug] pending navigation timed out waiting for navReady/authReady — running anyway', { navReady, authReady });
      pendingRun = null;
      run();
    }, PENDING_NAV_TIMEOUT_MS);
  }
}
